import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { on } from "../../../core/entity/handler";
import { Machine, machineKey } from "../../../game/Machine";
import { BenchGroup, benchGroupAt } from "../../../game/bench-work/bench-group";
import {
  BenchScript,
  benchGroupWork,
} from "../../../game/bench-work/workpiece";
import { projectProgression } from "../../../sim/projection";
import { PalletNail, ToolItem } from "../../../game/Materials";
import { ToolId } from "../../../game/Tool";
import { scenePlaying } from "../../../sim/commands/story-commands";
import {
  mountStoredTool,
  mountTool,
} from "../../../sim/commands/tool-commands";
import {
  floorMachines,
  MachineEntity,
} from "../../../sim/entities/MachineEntity";
import { Player } from "../../../sim/entities/Player";
import { ShellStore } from "../../ShellStore";
import { fitToStage, StageFit, StageRect } from "./stageMath";

/** How long the lean-in takes, each way. */
const DIVE_SECONDS = 0.65;

/** The screen the run is fitted into, inset for the dive's chrome: the
 * tool rail and the plan pile under it down the left edge, the top
 * bar's band across the top, the under-bench drawer's pull and the key
 * hints' stack along the bottom, a margin on the right. Each band
 * clears its chrome's full footprint — wood framed up under a chip
 * would be wood the hand can't reach, since the chip catches the
 * pointer first. */
const TOP_CHROME_PX = 120;
/** The trays along the bottom (BenchTrayView), the key hints between
 * them, and the upgrade pull above the shelf tray. */
const BOTTOM_CHROME_PX = 108;
const LEFT_CHROME_PX = 128;
const RIGHT_CHROME_PX = 24;
/** On the body while a hand holds something; index.css hides the cursor under it. */
const HAND_HELD_CLASS = "bench-hand-held";

/**
 * Where a tool in hand was taken from — and so where Escape puts it
 * back. Taking a tool up leaves the sim alone: one off the rail still
 * hangs there (faded on its hook), one out of the arms is still in the
 * inventory, one off the shelf still lies under the bench; only the
 * picture lifts it. It leaves its source for good when it is put down
 * somewhere else or used (`settleHeldTool`).
 */
export type ToolSource =
  | { readonly kind: "hook" }
  | { readonly kind: "arms"; readonly itemId: string }
  | {
      readonly kind: "shelf";
      readonly itemId: string;
      /** machineKey of the table whose shelf it lies on. */
      readonly memberKey: string;
    };

/**
 * One thing the hands can hold at the bench: a tool taken up from
 * wherever it lay, or a bar clamp or the glue bottle off the rail. The
 * clamp and the bottle grab exactly like the tools do, even though
 * under the hood a clamp is a pooled supply (game/Clamp.ts) and glue is
 * free — the hand doesn't care what the registry calls it.
 */
export type BenchHand =
  | { kind: "tool"; toolId: ToolId; from: ToolSource }
  | { kind: "clamp" }
  | { kind: "glue" };

function sameHand(a: BenchHand | null, b: BenchHand): boolean {
  return (
    a !== null &&
    a.kind === b.kind &&
    (a.kind !== "tool" || b.kind !== "tool" || a.toolId === b.toolId)
  );
}

/** The opened bench: its run, and where that run lands on screen. */
export interface BenchStage {
  readonly group: BenchGroup;
  readonly opened: Machine;
  /** Inches to screen pixels, for the stage between the chrome bands. */
  readonly fit: StageFit;
}

/** Motion the player has asked not to see is pinned, not played. */
function reducedMotion(): boolean {
  return (
    globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  );
}

/**
 * Which bench the player is leaned over (migration phase 7) — the old
 * benchSceneSlot's open/closed half as a shell entity. E at a
 * worktable opens the dive (the phase-4 note's deferral); Escape or the
 * step-back tag closes it. Transient UI state, never serialized: a reload stands the
 * player back up, exactly as the old shell's remount did.
 *
 * The world keeps ticking underneath — the dive holds only the walk
 * (the body stays in its working stance at the bench), not the sim.
 */
export class BenchDive extends BaseEntity implements Entity {
  id = "benchDive";
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  /** machineKey of the opened bench, or null on the shop floor. */
  openBenchKey: string | null = null;

  /**
   * The bench the view is still rolling back off, once the player has
   * stood up: the gestures stop the moment `openBenchKey` clears, but
   * the picture keeps the bench until the lean-in has run backwards.
   */
  closingKey: string | null = null;

  /**
   * How far the lean-in has run, 0..1. Opening a bench doesn't cut to
   * it — the view eases in from the bench's own footprint on the floor
   * — and standing up runs the same ramp backwards. Reduced motion pins
   * it, and nothing on the surface answers the pointer until it lands
   * (the old view's `settled`).
   */
  dive = 0;

  /**
   * What the hands hold — the bench's mode selector (docs/bench-work.md
   * decision 0). Applying it to a valid target IS the operation, so a
   * hammer over a staged pallet is pry mode and nothing else is. A tool
   * is taken up from the rail, the arms, or the shelf alike (its
   * `from` says which, and where Escape returns it); a bar clamp and
   * the glue bottle hang on the rail with the tools and are grabbed the
   * same way. One field means one thing in the hands at a time by
   * construction.
   */
  held: BenchHand | null = null;

  /**
   * Whether the plan drawer is spread across the view. The pile that
   * opens it sits under the tool rail (BenchPlanPile) and the drawer
   * itself is a whole-view sheet (PlanBrowser), so the state lives here
   * between them — and folds with the dive, like the hands empty.
   */
  plansOpen = false;

  /** The tool in hand, when the hand is holding one. */
  get heldTool(): ToolId | null {
    return this.held?.kind === "tool" ? this.held.toolId : null;
  }

  /** Where the tool in hand came from, or null with no tool in hand. */
  heldToolSource(): ToolSource | null {
    return this.held?.kind === "tool" ? this.held.from : null;
  }

  /** The tray item the hand lifted, when the tool in hand came out of
   * a tray: it is still there in the sim, and the tray hides its tile. */
  heldItemId(): string | null {
    const from = this.heldToolSource();
    return from && from.kind !== "hook" ? from.itemId : null;
  }

  get holdingClamp(): boolean {
    return this.held?.kind === "clamp";
  }

  get holdingGlue(): boolean {
    return this.held?.kind === "glue";
  }

  /** The nail the hammer is over, if any — the ring that warms. */
  hoveredNail: PalletNail | null = null;

  /** The pull playing out right now: the nail that just left, and how
   * much of the claw's lever is left to draw (seconds). */
  prying: { nail: PalletNail; secondsLeft: number } | null = null;

  open(bench: MachineEntity): void {
    const key = machineKey(bench.state);
    // Re-opening the bench being rolled back off just re-leans in from
    // wherever the ramp is; a different bench starts its own dive.
    if (this.closingKey !== null && this.closingKey !== key) this.dive = 0;
    this.openBenchKey = key;
    this.closingKey = null;
    this.invalidateStage();
    if (reducedMotion()) this.dive = 1;
    this.bump();
  }

  close(): void {
    if (this.openBenchKey === null) return;
    this.closingKey = this.openBenchKey;
    this.openBenchKey = null;
    if (reducedMotion()) {
      this.dive = 0;
      this.closingKey = null;
    }
    // Standing up empties the hands: whatever was taken down — a tool,
    // a clamp, the bottle — goes back on the rail, as the old view's
    // unmount did.
    this.held = null;
    this.plansOpen = false;
    this.hoveredNail = null;
    this.prying = null;
    this.invalidateStage();
    this.bump();
  }

  /** Spread the plan drawer open, or put it away. */
  setPlansOpen(open: boolean): void {
    if (this.plansOpen === open) return;
    this.plansOpen = open;
    this.bump();
  }

  /** Put something in the hands, or empty them (null). Taking one
   * thing always puts back whatever else was held. */
  hold(hand: BenchHand | null): void {
    this.held = hand;
    this.hoveredNail = null;
    this.bump();
  }

  /** The rail's click: take the thing in hand, or — already holding
   * it — put it back. */
  toggleHand(hand: BenchHand): void {
    this.hold(sameHand(this.held, hand) ? null : hand);
  }

  /** Take a tool that hangs on the opened bench's rail in hand. */
  takeRailTool(toolId: ToolId): void {
    this.hold({ kind: "tool", toolId, from: { kind: "hook" } });
  }

  /**
   * The tool in hand hangs on the opened bench's rail from here on —
   * the moment it is put to work. One off the rail already does; one
   * taken up out of the arms or off the shelf is mounted now, and its
   * source becomes the hook. False when the rail can't take it (the
   * offer already said so: `toolOperationFor` reads a tool in hand as
   * if hung, so this only fails if the world moved under the hand).
   */
  settleHeldTool(): boolean {
    const held = this.held;
    if (held?.kind !== "tool") return false;
    const from = held.from;
    if (from.kind === "hook") return true;
    const bench = this.openBench();
    if (!bench) return false;
    let hung = false;
    if (from.kind === "arms") {
      const item = this.game.entities
        .tryGetSingleton(Player)
        ?.inventory.find(
          (m): m is ToolItem => m.id === from.itemId && m.type === "tool",
        );
      hung = item !== undefined && mountTool(this.game, bench, item);
    } else {
      const holder = this.benchFor(from.memberKey);
      const item = holder?.state.storedMaterials?.find(
        (m): m is ToolItem => m.id === from.itemId && m.type === "tool",
      );
      hung =
        holder !== null &&
        item !== undefined &&
        mountStoredTool(this.game, holder, bench, item);
    }
    if (!hung) return false;
    this.held = { kind: "tool", toolId: held.toolId, from: { kind: "hook" } };
    this.bump();
    return true;
  }

  /**
   * Whether the tool in hand is still where its source says: the world
   * can move under the hand (a fixture load, a neighbour's table picked
   * up), and a hand holding a tool that no longer exists holds nothing.
   */
  private heldToolExists(): boolean {
    const held = this.held;
    if (held?.kind !== "tool") return true;
    const from = held.from;
    switch (from.kind) {
      case "hook":
        return this.openBench()?.state.tools.includes(held.toolId) ?? false;
      case "arms":
        return (
          this.game.entities
            .tryGetSingleton(Player)
            ?.inventory.some((m) => m.id === from.itemId) ?? false
        );
      case "shelf":
        return (
          this.benchFor(from.memberKey)?.state.storedMaterials?.some(
            (m) => m.id === from.itemId,
          ) ?? false
        );
    }
  }

  /** Whether the hands are carrying anything at all. */
  handsFull(): boolean {
    return this.held !== null;
  }

  /**
   * At a bench the pointer *is* the hand, so the right button puts back
   * whatever it's holding — the same move Escape makes, without
   * reaching for the keyboard.
   */
  @on("rightDown")
  onRightDown() {
    if (this.openBenchKey === null || !this.handsFull()) return;
    this.hold(null);
  }

  /** Whether the lean-in has landed: until it does, the surface is a
   * picture in motion. The trays and the rest of the picture read this;
   * the gestures read `workable()`. */
  settled(): boolean {
    return this.openBenchKey !== null && this.dive >= 1;
  }

  /**
   * Whether the surface answers the hand: the lean-in has landed (a
   * press mid-dive would land where a piece is about to be rather than
   * where it looks) and no story scene has the screen — a thought comes
   * up over the bench view the moment the work under the hand is done,
   * and its box takes the pointer along with the keys.
   */
  workable(): boolean {
    return this.settled() && !scenePlaying(this.game);
  }

  // -------------------------------------------------------------------
  // The stage, dive-owned (issue #230, phase 2). The run, its screen
  // fit, and the live script used to be re-derived by every gesture and
  // view helper each frame; they are cached here and dropped whenever a
  // domain event says the world they were derived from moved. Dispatch
  // is synchronous, so a command's own commit invalidates before the
  // next read — the freshness rule the old per-call derivation protected
  // (a press claims a workpiece; the next read must not show it lying on
  // the pile) holds without any per-frame rebuilding.
  // -------------------------------------------------------------------

  private runCache: { group: BenchGroup; opened: Machine } | null = null;
  private runValid = false;
  private stageCache: BenchStage | null = null;
  private stageRun: { group: BenchGroup; opened: Machine } | null = null;
  private stageWidth = 0;
  private stageHeight = 0;
  private workCache: { machine: Machine; script: BenchScript } | null = null;
  private workValid = false;

  /** The displayed bench's run, or null when nobody is leaned over one. */
  run(): { group: BenchGroup; opened: Machine } | null {
    const bench = this.displayedBench();
    if (!bench) return null;
    if (!this.runValid) {
      const machines = floorMachines(this.game);
      const key = machineKey(bench.state);
      const opened = machines.find(
        (machine) => machineKey(machine.state) === key,
      );
      this.runCache = opened
        ? { group: benchGroupAt(machines, opened), opened }
        : null;
      this.runValid = true;
    }
    return this.runCache;
  }

  /** That run plus its place on screen (null with no renderer). */
  stage(): BenchStage | null {
    const renderer = this.game.renderer;
    const run = this.run();
    if (!renderer || !run) return null;
    const width = renderer.getWidth();
    const height = renderer.getHeight();
    if (
      this.stageCache === null ||
      this.stageRun !== run ||
      this.stageWidth !== width ||
      this.stageHeight !== height
    ) {
      const stage: StageRect = {
        x: LEFT_CHROME_PX,
        y: TOP_CHROME_PX,
        width: width - LEFT_CHROME_PX - RIGHT_CHROME_PX,
        height: height - TOP_CHROME_PX - BOTTOM_CHROME_PX,
      };
      const fit = fitToStage(
        { widthIn: run.group.widthIn, heightIn: run.group.heightIn },
        stage,
      );
      this.stageCache = { group: run.group, opened: run.opened, fit };
      this.stageRun = run;
      this.stageWidth = width;
      this.stageHeight = height;
    }
    return this.stageCache;
  }

  /**
   * What the run is doing right now: the script the pure engine reads
   * out of the tables' state, and which table is running it.
   */
  work(): { machine: Machine; script: BenchScript } | null {
    const run = this.run();
    if (!run) return null;
    if (!this.workValid) {
      const work = benchGroupWork(
        run.group.members,
        run.opened,
        projectProgression(this.game),
      );
      this.workCache = work.script
        ? { machine: work.machine, script: work.script }
        : null;
      this.workValid = true;
    }
    return this.workCache;
  }

  private invalidateStage(): void {
    this.runValid = false;
    this.runCache = null;
    this.stageCache = null;
    this.stageRun = null;
    this.workValid = false;
    this.workCache = null;
  }

  @on("machineAdded")
  onMachineAdded() {
    this.invalidateStage();
  }

  @on("machineRemoved")
  onMachineRemoved() {
    this.invalidateStage();
  }

  @on("machineStateChanged")
  onMachineStateChanged() {
    this.invalidateStage();
  }

  @on("progressionChanged")
  onProgressionChanged() {
    // Unlocks decide which scripts the run can offer, not where it lies.
    this.workValid = false;
    this.workCache = null;
  }

  @on("worldLoaded")
  onWorldLoaded() {
    this.invalidateStage();
    this.groupKeysFor = null;
    this.groupKeys = null;
  }

  /** The opened bench's live entity, or null once it's out of reach. */
  openBench(): MachineEntity | null {
    return this.benchFor(this.openBenchKey);
  }

  /** The bench the view is drawing — the open one, or the one still
   * rolling back off the screen. */
  displayedBench(): MachineEntity | null {
    return this.benchFor(this.openBenchKey ?? this.closingKey);
  }

  /**
   * The machineKeys of every table in the displayed bench's group, or
   * null on the shop floor. The dive's close-up covers the whole run of
   * pushed-together tables, so the shop view hides all their staged
   * stock, not just the opened one's (see MachineView). Cached by the
   * displayed key: the group can't change while the player is leaned
   * over it.
   */
  displayedGroupKeys(): ReadonlySet<string> | null {
    const key = this.openBenchKey ?? this.closingKey;
    if (key === null) {
      this.groupKeysFor = null;
      this.groupKeys = null;
      return null;
    }
    if (this.groupKeysFor !== key) {
      const machines = floorMachines(this.game);
      const opened = machines.find(
        (machine) => machineKey(machine.state) === key,
      );
      this.groupKeysFor = key;
      this.groupKeys = opened
        ? new Set(
            benchGroupAt(machines, opened).members.map((member) =>
              machineKey(member.machine.state),
            ),
          )
        : new Set([key]);
    }
    return this.groupKeys;
  }
  private groupKeysFor: string | null = null;
  private groupKeys: ReadonlySet<string> | null = null;

  private benchFor(key: string | null): MachineEntity | null {
    if (key === null) return null;
    for (const machine of this.game.entities.byConstructor(MachineEntity)) {
      if (machineKey(machine.state) === key) {
        return machine;
      }
    }
    return null;
  }

  onDestroy() {
    document.body.classList.remove(HAND_HELD_CLASS);
  }

  @on("tick")
  onTick(dt: number) {
    // Whatever the hand holds IS the pointer (the icon cursor in
    // BenchStageMarker, the glue view's clamp ghost) — the native arrow
    // under it would read as two hands. Hidden page-wide, not on the
    // canvas alone: the icon keeps riding the pointer across the tool
    // rail and the rest of the chrome (index.css owns the rule).
    document.body.classList.toggle(
      HAND_HELD_CLASS,
      this.openBenchKey !== null && this.held !== null,
    );

    // The lean-in runs on real time, like the truck's roll: forward
    // while a bench is open, backwards once the player stands up.
    const target = this.openBenchKey === null ? 0 : 1;
    if (this.dive !== target) {
      const step = dt / DIVE_SECONDS;
      this.dive =
        target > this.dive
          ? Math.min(1, this.dive + step)
          : Math.max(0, this.dive - step);
      if (this.dive === 0 && this.closingKey !== null) this.closingKey = null;
      this.bump();
    }
    if (this.openBenchKey === null) return;
    if (!this.heldToolExists()) this.hold(null);
    if (this.prying) {
      this.prying =
        this.prying.secondsLeft <= dt
          ? null
          : { ...this.prying, secondsLeft: this.prying.secondsLeft - dt };
    }
    // The dive folds when its bench stops making sense: the bench gone
    // (picked up by a fixture load), the player away, or no player at
    // all (quit to the menu).
    const player = this.game.entities.tryGetSingleton(Player);
    if (!player || player.away !== null || this.openBench() === null) {
      this.close();
    }
  }

  private bump(): void {
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }
}
